import React, { memo, FC, useMemo } from 'react';
import { IDataGridMadel } from '../../model';
import { Button } from '@mui/material';
import { useI18n } from 'AurionCR/components';
import { useExportToExcel } from '../../../hooks';
import { createGetProps } from '../requests';
import RefreshIcon from '@mui/icons-material/Refresh';
import GetAppIcon from '@mui/icons-material/GetApp';
import AddIcon from '@mui/icons-material/Add';
import { format } from 'date-fns';
import { APP_FORMAT_EXCEL_DATE } from 'configs/const';
import { BarControlKey } from './bar-overflow';

const ControlsDefault: FC<
  React.PropsWithChildren<{
    model: IDataGridMadel;
    onTasks: (tasks: any[]) => void;
    filters: any;
    pagination: any;
    sortable: any;
    /**
     * N2b (T3): below md, the buttons the bar shows (`splitBarControls`); the
     * rest are in the kebab. Absent = every configured button (the desktop).
     */
    visibleKeys?: BarControlKey[];
  }>
> = ({ model, filters, onTasks, pagination, sortable, visibleKeys }) => {
  const { t } = useI18n();
  const { exportToExcel } = useExportToExcel();
  const controls = model?.toolbar?.controlsDefault;
  const isDisableResetFilters = useMemo(() => {
    return controls?.resetFilters && model?.toolbar?.filters?.length
      ? !(model?.toolbar?.filters || []).some((value) => {
          const val = filters[value.field];
          return val && val !== value.default;
        })
      : false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controls?.resetFilters, filters, model?.toolbar?.filters]);
  // handles
  const onResetFilters = () => {
    const filters = (model.toolbar.filters || []).reduce((acc: any, value) => {
      acc[value.field] = value.default || '';
      return acc;
    }, {});
    onTasks([['filters', filters], ['getData']]);
  };
  const onExport = () => {
    const params = { ...createGetProps({ model, pagination, filters, sortable }) };
    const title = t(model?.toolbar?.excelTitle || model?.toolbar?.title || '');
    params.params.Take = null;
    params.params.Skip = 0;
    exportToExcel({
      ...params,
      columns: model.columns,
      title: `${format(new Date(), APP_FORMAT_EXCEL_DATE)}-${title}`,
    }).catch((e) => {
      console.error('export', model, e);
    });
  };

  const show = (key: BarControlKey) =>
    Boolean(controls?.[key]) && (!visibleKeys || visibleKeys.includes(key));

  if (!show('refresh') && !show('new') && !show('resetFilters') && !show('export')) {
    return null;
  }

  return (
    <ul className="toolbar-controls">
      {show('resetFilters') && controls?.resetFilters && (
        <li>
          <Button
            className="btn-reset-filters"
            title={controls.resetFilters.title || ''}
            variant="contained"
            color="primary"
            // size="small"
            onClick={onResetFilters}
            disabled={isDisableResetFilters}
            startIcon={<RefreshIcon />}
          >
            {t(controls.resetFilters.title || '')}
          </Button>
        </li>
      )}
      {show('new') && controls?.new && (
        <li>
          <Button
            className="btn-new"
            // @ts-ignore
            title={controls.new.title || ''}
            variant="contained"
            color="secondary"
            // size="small"
            onClick={(_) => onTasks([['onHandle', { type: 'new' }]])}
            startIcon={<AddIcon />}
          >
            {t(controls.new.title || '')}
          </Button>
        </li>
      )}
      {show('export') && controls?.export && (
        <li>
          <Button
            className="btn-export"
            title={controls.export.title || ''}
            variant="contained"
            color="primary"
            // size="small"
            onClick={onExport}
            disabled={!pagination?.total}
            startIcon={<GetAppIcon />}
          >
            {t(controls.export.title || '')}
          </Button>
        </li>
      )}
      {show('refresh') && controls?.refresh && (
        <li>
          <Button
            className="btn-refresh"
            title={controls.refresh.title || ''}
            variant="contained"
            color="primary"
            // size="small"
            onClick={(_) => onTasks([['getData']])}
            startIcon={<RefreshIcon />}
          >
            {t(controls.refresh.title || '')}
          </Button>
        </li>
      )}
    </ul>
  );
};

export default memo(ControlsDefault);
